import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { AnimatePresence, motion } from 'framer-motion';
import QuestionnaireContainer from '@/features/funnel/questionnaires/components/QuestionnaireContainer';
import type { IQuestionnaire } from '@/types/features/questionnaire/question';
import FunnelNavigation from '@/components/ui/FunnelNavigation';
import { preQuestionnaireStore } from '@/store/preQuestionnaireStore';
import { productSlugStore } from '@/store/productSlugStore';
import { rptStore } from '@/store/rptStore';
import { authStore } from '@/store/authStore';
import { useFunnelStore } from '@/store/funnelStore';
import { useInterstitialStore } from '@/store/interstitial/store';
import { useFetchData, useMutateData } from '@/utils/ReqUtil';
import Error from '@/components/ui/Error';
import NotFound from '@/pages/NotFound';
import type { ApiSuccessResponse, FunnelNavigationResponse } from '@/types/api/shared';
import useEXTE from '@/hooks/useEXTE';
import useHandleError, { type HandleErrorProps } from '@/hooks/useHandleError';
import FunnelContainer from '@/components/layouts/FunnelContainer';
import { segment } from '@/lib/analytics/segment';
import { useInterstitialControl } from '@/hooks/useInterstitialControl';
import useGoogleOTPFlow from '@/features/funnel/questionnaires/hooks/useGoogleOTPFlow';
import { getProductGroup } from '@/constants/products';

let lastTrackedQuestionIndex: number | null = null;

interface PreQuestionnaireAnswersResponseData {
  data: { message: string };
  next_path: FunnelNavigationResponse;
}

const PreQuestionnaire: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const rpt = searchParams.get('rpt');
  const subscriptionType = searchParams.get('subscription_type');
  const { setSlug, slug: storeSlug } = productSlugStore();
  const { setRPT, clearRPT } = rptStore();
  const { accessToken } = authStore();
  const { setPreviousPath, isGoingBack, setGoingBack } = useFunnelStore();
  const { isTransitioning } = useInterstitialStore();
  const {
    questions,
    currentQuestionIndex,
    currentQuestionAnswer,
    initialize,
    setCurrentQuestionAnswer,
    goToNextQuestion,
    clear,
    addOrUpdateAnswer,
    trackQuestionAnswered,
  } = preQuestionnaireStore();
  const { trackEvent } = useEXTE();

  const { mutate } = useMutateData('POST', `/products/${slug}/pre_questionnaire_answers`);
  const handleError = useHandleError();

  const {
    handleCircularProgress,
    handleMultiTestimonial,
    handleSleepStats,
    handleStatistics,
    saveCurrentInterstitialForQuestion,
    handleSecondStatisticsChain,
  } = useInterstitialControl();

  // Manage Google OTP (One Time Purchase) flow in localStorage
  useGoogleOTPFlow(subscriptionType, slug);

  useEffect(() => {
    if (rpt) {
      setRPT(rpt);
    }
  }, [rpt, setRPT]);

  useEffect(() => {
    if (slug) {
      setSlug(slug);
    }

    if (slug !== storeSlug) {
      clear();
    }
  }, [slug, setSlug, storeSlug, clear]);

  const { isLoading, isError, error, refetch, isFetched, data } = useFetchData<
    ApiSuccessResponse<IQuestionnaire>
  >({
    key: ['pre-questionnaire', slug || ''],
    url: `/products/${slug}/pre_questionnaire`,
    enabled: !!slug,
    retry: 2,
  });

  const questionnaire = data?.data ?? null;

  useEffect(() => {
    if (isFetched && questionnaire?.questions?.length) {
      // Check for circular progress interstitial first
      if (!isTransitioning) {
        // If user came directly to pre-questionnaire, redirect to interstitial first
        const currentPath = window.location.pathname;
        if (currentPath.includes('/pre_questionnaire')) {
          if (handleCircularProgress()) {
            return; // Don't initialize questionnaire if interstitial is shown
          }
        }

        // Only show circular progress if we're at the beginning (no questions answered yet)
        const hasAnsweredQuestions =
          Object.keys(preQuestionnaireStore.getState().answers).length > 0;
        if (!hasAnsweredQuestions && slug) {
          if (handleCircularProgress()) {
            return; // Don't initialize questionnaire if interstitial is shown
          }
        }
      }

      // Only initialize questionnaire if no interstitial was shown
      initialize(questionnaire.questions);
    } else if (isFetched && !accessToken) {
      navigate('/signup');
    }
  }, [
    isFetched,
    questionnaire,
    initialize,
    navigate,
    accessToken,
    isTransitioning,
    slug,
    handleCircularProgress,
  ]);

  useEffect(() => {
    const currentQuestion = questions[currentQuestionIndex];
    if (currentQuestion?.question_text) {
      segment.page({
        product_slug: slug,
        question_text: currentQuestion.question_text.toLowerCase(),
        question_index: currentQuestionIndex + 1,
      });
    }

    if (lastTrackedQuestionIndex === currentQuestionIndex) return;

    if (typeof currentQuestionIndex === 'number') {
      lastTrackedQuestionIndex = currentQuestionIndex;
      trackEvent(`g_eve00${String(currentQuestionIndex + 3)}`);
    }
  }, [currentQuestionIndex, trackEvent, questions, slug]);

  const showCompletionInterstitial = () => {
    if (currentQuestionIndex === 1 && handleSecondStatisticsChain('/signup')) {
      return true;
    }
    if (handleSleepStats()) {
      return true;
    }
    if (handleMultiTestimonial()) {
      return true;
    }
    return false;
  };

  const showMidQuestionInterstitial = () => {
    if (currentQuestionIndex === 0 && handleStatistics()) {
      return true;
    }

    if (currentQuestionIndex === 1) {
      const productGroup = getProductGroup(storeSlug);
      if (productGroup === 'rugiet-ed') {
        const { setInterstitial } = useInterstitialStore.getState();
        // Only preserve subscription_type query param
        const subscriptionType = searchParams.get('subscription_type');
        const searchSuffix = subscriptionType ? `?subscription_type=${subscriptionType}` : '';
        setInterstitial({
          template: 'statistics',
          templateData: {
            type: 'statistics',
            data: {
              headline: (
                <>
                  <span className="font-bold">80%</span> of men prefer Rugiet over other
                  <br /> ED treatments*
                </>
              ),
              imageUrl: '/assets/images/interstitials/statistics-alt.jpg',
              disclaimer: (
                <>
                  *Based on a survey of 114 active Rugiet Ready
                  <br /> patients, conducted in May 2025.
                </>
              ),
            },
          },
          nextPath: `/products/${slug}/pre_questionnaire`,
        });
        navigate(`/interstitial/statistics${searchSuffix}`);
        return true;
      }
    }

    return false;
  };

  const handleNext = async () => {
    if (questions.length === currentQuestionIndex + 1) {
      if (accessToken) {
        const currentQuestion = questions[currentQuestionIndex];
        const currentAnswer =
          preQuestionnaireStore.getState().currentQuestionAnswer[currentQuestion.id] || '';
        const answerStr = Array.isArray(currentAnswer) ? currentAnswer.join(',') : currentAnswer;

        addOrUpdateAnswer({
          question_id: currentQuestion.id,
          answer: answerStr,
        });

        trackQuestionAnswered(currentQuestion, currentAnswer);

        const storedAnswers = preQuestionnaireStore.getState().answers;
        const formattedAnswers = storedAnswers.reduce(
          (acc, answer) => {
            acc[answer.question_id] = answer.answer.split(',');
            return acc;
          },
          {} as Record<string, string[]>,
        );

        try {
          mutate(
            { questionnaire_answers: formattedAnswers },
            {
              onSuccess: (resData) => {
                const { next_path } = resData as PreQuestionnaireAnswersResponseData;
                if (!next_path) return;
                const { product_slug, user_questionnaire_id, question_id, path_type } = next_path;
                let redirectPath = '';
                if (path_type === 'user-bmi') {
                  redirectPath = `/${path_type}`;
                } else {
                  redirectPath = `/products/${product_slug}/user_questionnaires/${user_questionnaire_id}/questions/${question_id}`;
                }
                localStorage.setItem('currentPath', redirectPath);
                saveCurrentInterstitialForQuestion('/products/rugiet/pre_questionnaire');
                clear();
                clearRPT();
                navigate(redirectPath);
              },
              onError: (error) => handleError(error as HandleErrorProps<Error>),
            },
          );
        } catch {
          // Intentionally empty
        }
      } else {
        trackEvent('g_eve006');

        if (showCompletionInterstitial()) {
          return;
        }

        goToNextQuestion();
        setPreviousPath('');
        navigate('/signup');
      }
    } else {
      setTimeout(() => {
        goToNextQuestion();
        showMidQuestionInterstitial();
      }, 100);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];

  useEffect(() => {
    setPreviousPath(currentQuestionIndex ? currentQuestion.id : '');
  }, [setPreviousPath, currentQuestionIndex, currentQuestion]);

  if (isLoading) return null;
  if (isError) return <Error message={error?.message} retry={refetch} />;
  if (isFetched && !questionnaire?.questions?.length && accessToken) return <NotFound />;

  if (!questions.length) return null;

  return (
    <FunnelContainer>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, x: isGoingBack ? -50 : 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: isGoingBack ? 50 : -50 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col flex-1"
        >
          <QuestionnaireContainer
            key={currentQuestion.id}
            questions={[currentQuestion]}
            getQuestionAnswer={(answerObj) => {
              const answer = answerObj[currentQuestion.id];
              setCurrentQuestionAnswer(currentQuestion.id, answer);
              if (currentQuestion.question_type === 'single_choice') {
                setGoingBack(false);
                handleNext();
                return;
              }
            }}
            questionAnswer={currentQuestionAnswer}
          />
          <FunnelNavigation
            isNextDisable={!currentQuestionAnswer[currentQuestion.id]?.toString().length}
            isPrevDisable
            nextUrl={questions.length === currentQuestionIndex + 1 ? '/signup' : ''}
            isNextHidden={currentQuestion.question_type === 'single_choice'}
            onNext={handleNext}
            preventNavigation={!!accessToken}
            useTabletCustomWidth
          />
        </motion.div>
      </AnimatePresence>
    </FunnelContainer>
  );
};

export default PreQuestionnaire;
